import { cloneDeep } from "lodash-es"

export type TradeGradeItem = {
  id?: number | string
  quantity?: number | string
  rate?: number
  daily_rate?: number
  variety_grade?: any
  rate_unit?: any
  quantity_unit?: any
  total_amount?: number
  result_id?: any
  [key: string]: any
}

export type TradeCommodityGst = {
  supplier_sgst: number
  supplier_cgst: number
  supplier_igst: number
  supplier_sgst_percentage: number
  supplier_cgst_percentage: number
  supplier_igst_percentage: number
  supplier_gst_auto_calculated: boolean
  seller_gst_attachments: any[]
  buyer_sgst: number
  buyer_cgst: number
  buyer_igst: number
  buyer_sgst_percentage: number
  buyer_cgst_percentage: number
  buyer_igst_percentage: number
  buyer_gst_auto_calculated: boolean
  buyer_gst_attachments: any[]
  _gstFromBackend: boolean
}

export type TradeCommodityBlock = {
  commodity?: any
  commodity_variety?: any
  plot_crop?: any
  quantity_unit?: any
  grades: TradeGradeItem[]
  gst: TradeCommodityGst
  total_quantity_kg?: number
}

export function createEmptyTradeCommodityBlock(
  partial?: Partial<TradeCommodityBlock>
): TradeCommodityBlock {
  return {
    grades: [{ rate: 0, quantity: 0 }],
    gst: {
      supplier_sgst: 0,
      supplier_cgst: 0,
      supplier_igst: 0,
      supplier_sgst_percentage: 0,
      supplier_cgst_percentage: 0,
      supplier_igst_percentage: 0,
      supplier_gst_auto_calculated: true,
      seller_gst_attachments: [],
      buyer_sgst: 0,
      buyer_cgst: 0,
      buyer_igst: 0,
      buyer_sgst_percentage: 0,
      buyer_cgst_percentage: 0,
      buyer_igst_percentage: 0,
      buyer_gst_auto_calculated: true,
      buyer_gst_attachments: [],
      _gstFromBackend: false,
    },
    ...partial,
  }
}

export function getUnitBaseConversion(unit?: any): number {
  return (
    Number(unit?.base_conversion ?? unit?.si_unit?.[0]?.base_conversion) || 0
  )
}

export function getCommodityParticularsTotals(
  block: TradeCommodityBlock,
  qcResults?: { template: { grade: { name: string } } }[]
) {
  const grades = block.grades?.length ? block.grades : [{ rate: 0 }]
  const totals = grades.reduce<{ quantity: number; total_cost: number }>(
    (acc, g) => {
      const qcResult = qcResults?.find(
        r => r.template.grade.name === g.variety_grade?.name
      )
      const baseRateKey: "daily_rate" | "rate" = qcResult
        ? "daily_rate"
        : "rate"
      const qty = Number(g.quantity) || 0
      const rate = Number(g[baseRateKey]) || 0
      return {
        quantity: acc.quantity + qty,
        total_cost: acc.total_cost + qty * rate,
      }
    },
    { quantity: 0, total_cost: 0 }
  )
  return {
    quantity: totals.quantity,
    total_cost: totals.total_cost,
    total_quantity_kg:
      totals.quantity * getUnitBaseConversion(block.quantity_unit),
  }
}

export function syncNestedTradeToFormValues(state: any): any {
  const stateCopy = cloneDeep(state)
  const blocks: TradeCommodityBlock[] | null =
    stateCopy.trade_commodity_items?.length > 0
      ? stateCopy.trade_commodity_items
      : null

  if (!blocks) {
    return stateCopy
  }

  blocks.forEach(block => {
    const blockUnit = block.quantity_unit ?? block.grades?.[0]?.quantity_unit
    block.quantity_unit = blockUnit
    block.grades?.forEach((grade: TradeGradeItem) => {
      if (blockUnit) {
        grade.quantity_unit = blockUnit
        if (!grade.rate_unit) {
          grade.rate_unit = blockUnit
        }
      }
    })
  })

  stateCopy.quantity_unit = blocks[0]?.quantity_unit

  const first = blocks[0]
  if (first?.commodity) {
    stateCopy.commodity = first.commodity
  }
  if (first?.commodity_variety) {
    stateCopy.commodity_variety = first.commodity_variety
  }
  if (first?.plot_crop) {
    stateCopy.plot_crop = first.plot_crop
  }

  const flatItems: TradeGradeItem[] = []
  blocks.forEach(block => {
    const grades = block.grades?.length ? block.grades : [{ rate: 0 }]
    grades.forEach(grade => {
      flatItems.push({
        ...grade,
        quantity_unit: grade.quantity_unit ?? block.quantity_unit,
        rate_unit:
          grade.rate_unit ?? grade.quantity_unit ?? block.quantity_unit,
      })
    })
  })

  stateCopy.trade_items =
    flatItems.length > 0 ? flatItems : stateCopy.trade_items ?? [{}]
  stateCopy.trade_commodity_items = blocks

  // Aggregate per-commodity GST into top-level fields for backward compat
  const agg = {
    supplier_sgst: 0,
    supplier_cgst: 0,
    supplier_igst: 0,
    supplier_sgst_percentage: 0,
    supplier_cgst_percentage: 0,
    supplier_igst_percentage: 0,
    buyer_sgst: 0,
    buyer_cgst: 0,
    buyer_igst: 0,
    buyer_sgst_percentage: 0,
    buyer_cgst_percentage: 0,
    buyer_igst_percentage: 0,
  }
  blocks.forEach(block => {
    const g = (block as any).gst
    if (!g) return
    agg.supplier_sgst += g.supplier_sgst || 0
    agg.supplier_cgst += g.supplier_cgst || 0
    agg.supplier_igst += g.supplier_igst || 0
    agg.supplier_sgst_percentage += g.supplier_sgst_percentage || 0
    agg.supplier_cgst_percentage += g.supplier_cgst_percentage || 0
    agg.supplier_igst_percentage += g.supplier_igst_percentage || 0
    agg.buyer_sgst += g.buyer_sgst || 0
    agg.buyer_cgst += g.buyer_cgst || 0
    agg.buyer_igst += g.buyer_igst || 0
    agg.buyer_sgst_percentage += g.buyer_sgst_percentage || 0
    agg.buyer_cgst_percentage += g.buyer_cgst_percentage || 0
    agg.buyer_igst_percentage += g.buyer_igst_percentage || 0
  })
  Object.assign(stateCopy, agg)

  return stateCopy
}

export function buildTradeCommodityItemsFromState(
  state: any
): TradeCommodityBlock[] {
  if (state.trade_commodity_items?.length) {
    return state.trade_commodity_items
  }

  const quantityUnit = state.quantity_unit
  const grades =
    state.trade_items?.filter((item: any) => item != null)?.length > 0
      ? state.trade_items.map((item: any) => ({
          ...item,
          quantity_unit: item.quantity_unit ?? quantityUnit,
          rate_unit: item.rate_unit ?? quantityUnit,
        }))
      : [{ rate: 0, quantity: 0 }]

  return [
    createEmptyTradeCommodityBlock({
      commodity: state.commodity,
      commodity_variety: state.commodity_variety,
      plot_crop: state.plot_crop,
      quantity_unit: quantityUnit,
      grades,
      gst: {
        supplier_sgst: state.supplier_sgst || 0,
        supplier_cgst: state.supplier_cgst || 0,
        supplier_igst: state.supplier_igst || 0,
        supplier_sgst_percentage: state.supplier_sgst_percentage || 0,
        supplier_cgst_percentage: state.supplier_cgst_percentage || 0,
        supplier_igst_percentage: state.supplier_igst_percentage || 0,
        supplier_gst_auto_calculated:
          state.supplier_gst_auto_calculated ?? true,
        seller_gst_attachments: state.seller_gst_attachments || [],
        buyer_sgst: state.buyer_sgst || 0,
        buyer_cgst: state.buyer_cgst || 0,
        buyer_igst: state.buyer_igst || 0,
        buyer_sgst_percentage: state.buyer_sgst_percentage || 0,
        buyer_cgst_percentage: state.buyer_cgst_percentage || 0,
        buyer_igst_percentage: state.buyer_igst_percentage || 0,
        buyer_gst_auto_calculated: state.buyer_gst_auto_calculated ?? true,
        buyer_gst_attachments: state.buyer_gst_attachments || [],
        _gstFromBackend: state._gstFromBackend ?? false,
      },
    }),
  ]
}

export function syncPackagingFlatToBlock(state: any): any {
  if (state.material_kind !== "packaging") return state

  const block: TradeCommodityBlock = {
    ...createEmptyTradeCommodityBlock(),
    ...(state.trade_commodity_items?.[0] || {}),
  }

  if (state.commodity) block.commodity = state.commodity
  if (state.commodity_variety) block.commodity_variety = state.commodity_variety
  block.quantity_unit = state.quantity_unit ?? block.quantity_unit

  if (!state.commodity && block.commodity) state.commodity = block.commodity
  if (!state.commodity_variety && block.commodity_variety) {
    state.commodity_variety = block.commodity_variety
  }

  const flatItems = (state.trade_items || []).filter((i: any) => i != null)
  block.grades = flatItems.length
    ? flatItems.map((item: any) => ({
        ...item,
        quantity_unit: item.quantity_unit ?? block.quantity_unit,
        rate_unit: item.rate_unit ?? item.quantity_unit ?? block.quantity_unit,
      }))
    : [{ rate: 0, quantity: 0 }]

  state.trade_commodity_items = [block]
  return state
}

export function inferAllowMultipleCommodity(state: any): boolean {
  if (state.allow_multiple_commodity !== undefined) {
    return Boolean(state.allow_multiple_commodity)
  }
  return (state.trade_commodity_items?.length ?? 0) > 1
}

export function hasMultipleVarietiesWithGst(state: any): boolean {
  const blocks: TradeCommodityBlock[] = state?.trade_commodity_items || []
  if (blocks.length < 2) return false
  return blocks.some(
    block =>
      (
        block.commodity_variety?.organisation_commodity_variety_configs?.[0]
          ?.taxes || []
      ).length > 0
  )
}

export function buildTaxesFromGst(
  gst?: Partial<TradeCommodityGst> | null
): { role: string; tax_name: string; percentage: number }[] {
  if (!gst) return []
  const taxRows: { role: string; tax_name: string; percentage: number }[] = []
  const push = (
    role: string,
    tax_name: string,
    percentage: number,
    amount: number
  ) => {
    if (percentage > 0 || amount > 0) {
      taxRows.push({ role, tax_name, percentage })
    }
  }
  push(
    "seller",
    "SGST",
    gst.supplier_sgst_percentage ?? 0,
    gst.supplier_sgst ?? 0
  )
  push(
    "seller",
    "CGST",
    gst.supplier_cgst_percentage ?? 0,
    gst.supplier_cgst ?? 0
  )
  push(
    "seller",
    "IGST",
    gst.supplier_igst_percentage ?? 0,
    gst.supplier_igst ?? 0
  )
  push("buyer", "SGST", gst.buyer_sgst_percentage ?? 0, gst.buyer_sgst ?? 0)
  push("buyer", "CGST", gst.buyer_cgst_percentage ?? 0, gst.buyer_cgst ?? 0)
  push("buyer", "IGST", gst.buyer_igst_percentage ?? 0, gst.buyer_igst ?? 0)
  return taxRows
}

export type TradeItemInputOptions = {
  defaultCurrency?: { id?: any }
  qcResults?: any[]
  removedTradeItems?: Record<string, any>
  includeId: boolean
  parentTradeItemIdFor?: (grade: TradeGradeItem) => any
}

export function buildTradeItemInputsFromBlocks(
  blocks: TradeCommodityBlock[],
  opts: TradeItemInputOptions
): any[] {
  const inputs: any[] = []
  blocks.forEach(block => {
    const grades = block.grades?.length ? block.grades : []
    grades.forEach(grade => {
      if (!grade) return
      if (grade?.id && opts.removedTradeItems?.[grade.id]) return
      if (grade?.variety_grade?.shared_id == null && !grade?.quantity) return
      const qcResult = opts.qcResults?.find(
        (result: any) =>
          result.template.grade.name === grade.variety_grade?.name
      )
      const supplierGstAutoCalculated =
        block.gst?.supplier_gst_auto_calculated ?? true
      const buyerGstAutoCalculated =
        block.gst?.buyer_gst_auto_calculated ?? true
      const taxes = buildTaxesFromGst(block.gst).filter(
        t =>
          (t.role === "seller" && !supplierGstAutoCalculated) ||
          (t.role === "buyer" && !buyerGstAutoCalculated)
      )
      inputs.push({
        ...(opts.includeId && grade.id ? { id: grade.id } : {}),
        ...(opts.parentTradeItemIdFor
          ? { parent_trade_item_id: opts.parentTradeItemIdFor(grade) }
          : {}),
        result_id: grade.result_id || qcResult?.shared_id,
        rate: grade.rate,
        daily_rate:
          grade.daily_rate || grade.daily_rate === 0
            ? Number(grade.daily_rate)
            : undefined,
        commodity_id: block.commodity?.shared_id ?? block.commodity?.id,
        commodity_variety_id:
          block.commodity_variety?.shared_id ?? block.commodity_variety?.id,
        plot_crop_id: block.plot_crop?.shared_id ?? block.plot_crop?.id,
        quantity: grade.quantity,
        unit_id:
          block.quantity_unit?.shared_id ??
          block.quantity_unit?.id ??
          grade.quantity_unit?.shared_id ??
          grade.quantity_unit?.id,
        variety_grade_id:
          grade.variety_grade?.shared_id ?? grade.variety_grade?.id,
        currency_id: opts.defaultCurrency?.id,
        supplier_gst_auto_calculated: supplierGstAutoCalculated,
        buyer_gst_auto_calculated: buyerGstAutoCalculated,
        batch_number: grade?.batch_number,
        ...(taxes.length ? { taxes } : {}),
      })
    })
  })
  return inputs
}

function buildBlockGstFromTaxes(
  taxes: any[] | undefined,
  supplierGstAutoCalculated: boolean,
  buyerGstAutoCalculated: boolean
): TradeCommodityGst {
  const gst = createEmptyTradeCommodityBlock().gst
  gst.supplier_gst_auto_calculated = supplierGstAutoCalculated
  gst.buyer_gst_auto_calculated = buyerGstAutoCalculated
  for (const t of taxes || []) {
    const role = (t.role || "").toLowerCase()
    const name = (t.tax_name || "").toLowerCase()
    const key = `${
      role === "seller" ? "supplier" : "buyer"
    }_${name}` as keyof TradeCommodityGst
    if (key in gst && typeof gst[key] === "number") {
      ;(gst as any)[key] = ((gst as any)[key] || 0) + (t.amount ?? 0)
    }
    const pctKey = `${key}_percentage` as keyof TradeCommodityGst
    if (pctKey in gst && typeof gst[pctKey] === "number") {
      const current = (gst as any)[pctKey] ?? 0
      const incoming = t.percentage ?? 0
      if (!current && incoming) {
        ;(gst as any)[pctKey] = incoming
      }
    }
  }
  return gst
}

export type TradeCommodityItemsBackendFallback = {
  commodity?: any
  commodity_variety?: any
  plot_crop?: any
  quantity_unit?: any
  taxes?: any[]
  supplier_gst_auto_calculated?: boolean
  buyer_gst_auto_calculated?: boolean
  seller_gst_attachments?: any[]
  buyer_gst_attachments?: any[]
}

export function buildTradeCommodityItemsFromBackend(
  tradeItems: any[],
  fallback: TradeCommodityItemsBackendFallback,
  opts?: { markGstFromBackend?: boolean }
): TradeCommodityBlock[] {
  const items = (tradeItems || []).filter(item => item != null)
  if (!items.length) return [createEmptyTradeCommodityBlock()]
  const markGstFromBackend = opts?.markGstFromBackend !== false

  const groups = new Map<string, any[]>()
  for (const item of items) {
    const key =
      String(item.commodity?.id ?? "default") +
      "::" +
      String(item.commodity_variety?.id ?? "default")
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key)!.push(item)
  }

  const blocks: TradeCommodityBlock[] = []
  for (const group of groups.values()) {
    const first = group[0]
    const hasCommodity = Boolean(first?.commodity?.id)

    const itemTaxes = group.flatMap(item => item.taxes || [])

    const gst = buildBlockGstFromTaxes(
      itemTaxes.length ? itemTaxes : fallback.taxes,
      hasCommodity
        ? first.supplier_gst_auto_calculated ?? true
        : fallback.supplier_gst_auto_calculated ?? true,
      hasCommodity
        ? first.buyer_gst_auto_calculated ?? true
        : fallback.buyer_gst_auto_calculated ?? true
    )
    if (!hasCommodity) {
      gst.seller_gst_attachments = fallback.seller_gst_attachments || []
      gst.buyer_gst_attachments = fallback.buyer_gst_attachments || []
    }

    blocks.push(
      createEmptyTradeCommodityBlock({
        commodity: hasCommodity
          ? {
              ...first.commodity?.commodity?.[0],
              shared_id: first.commodity?.id,
            }
          : fallback.commodity,
        commodity_variety: hasCommodity
          ? {
              ...first.commodity_variety?.commodity_variety?.[0],
              shared_id: first.commodity_variety?.id,
            }
          : fallback.commodity_variety,
        plot_crop:
          hasCommodity && first.plot_crop?.plot_crop?.[0]
            ? {
                ...first.plot_crop?.plot_crop?.[0],
                shared_id: first.plot_crop?.id,
              }
            : fallback.plot_crop,
        quantity_unit: first?.quantity_unit
          ? {
              ...first.quantity_unit?.si_unit?.[0],
              shared_id: first.quantity_unit?.id,
            }
          : fallback.quantity_unit,
        grades: group.map(item => ({
          ...item,
          quantity_unit: item.quantity_unit
            ? {
                ...item.quantity_unit?.si_unit?.[0],
                shared_id: item.quantity_unit?.id,
              }
            : undefined,
          rate_unit: item.rate_unit
            ? {
                ...item.rate_unit?.si_unit?.[0],
                shared_id: item.rate_unit?.id,
              }
            : undefined,
        })),
        gst: { ...gst, _gstFromBackend: markGstFromBackend },
      })
    )
  }

  return blocks
}
